<template>
   <dashboard-layout>
      <v-container fluid class="down-top-padding py-0">
         <BaseBreadcrumb :title="page.title" :breadcrumbs="breadcrumbs"></BaseBreadcrumb>

         <v-row dense class="mb-3">
            <v-col cols="12" sm="6" md v-for="summary in summaries" :key="summary.label">
               <v-card outlined :class="`summary-card ${summary.className}`">
                  <v-card-text class="d-flex align-center">
                     <v-avatar :color="summary.color" size="46" class="mr-3"><v-icon dark>{{ summary.icon }}</v-icon></v-avatar>
                     <div><div class="text-h5 font-weight-bold">{{ summary.value }}</div><div class="caption">{{ summary.label }}</div></div>
                  </v-card-text>
               </v-card>
            </v-col>
         </v-row>

         <v-card>
            <v-toolbar dark color="primary">
               <v-icon left>mdi-school-outline</v-icon>
               <v-toolbar-title>Student to Graduate Applications</v-toolbar-title>
               <v-spacer></v-spacer>
               <v-text-field v-model="search" append-icon="mdi-magnify" label="Search applicant" single-line hide-details dark class="admin-search"></v-text-field>
            </v-toolbar>

            <v-alert v-if="flashMessage" type="success" dense text class="ma-4 mb-0">{{ flashMessage }}</v-alert>
            <v-alert v-if="actionMessage" :type="actionMessageType" dense text class="ma-4 mb-0">{{ actionMessage }}</v-alert>

            <v-data-table :headers="headers" :items="applications" :search="search" :items-per-page="10" class="elevation-0">
               <template v-slot:[`item.applicant`]='{ item }'>
                  <div class="py-2"><strong>{{ item.fullname }}</strong><div class="caption">{{ item.memberid || 'No Member ID' }}</div><div class="caption grey--text">{{ item.contactemail }}</div></div>
               </template>

               <template v-slot:[`item.certificate`]='{ item }'>
                  <v-btn v-if="item.applgrad_certificate" small text color="primary" :href="certificateUrl(item)" target="_blank"><v-icon left small>mdi-file-certificate-outline</v-icon>View</v-btn>
                  <v-chip v-else x-small color="red" dark>Missing</v-chip>
               </template>

               <template v-slot:[`item.proposer`]='{ item }'>
                  <member-summary :member="item.graduate_proposer"></member-summary>
                  <v-chip x-small dark :color="endorsementColor(item.applgrad_proposer_status)">
                     {{ endorsementLabel(item.applgrad_proposer_status) }}
                  </v-chip>
               </template>
               <template v-slot:[`item.seconder`]='{ item }'>
                  <member-summary :member="item.graduate_seconder"></member-summary>
                  <v-chip x-small dark :color="endorsementColor(item.applgrad_seconder_status)">
                     {{ endorsementLabel(item.applgrad_seconder_status) }}
                  </v-chip>
               </template>

               <template v-slot:[`item.applgradstatus`]='{ item }'>
                  <v-chip small dark :color="statusColor(item.applgradstatus)">{{ statusLabel(item.applgradstatus) }}</v-chip>
                  <div v-if="item.applgrad_notes" class="caption mt-1 notes-cell">{{ item.applgrad_notes }}</div>
               </template>

               <template v-slot:[`item.applgrad_submitted_at`]='{ item }'>{{ formatDate(item.applgrad_submitted_at) }}</template>

               <template v-slot:[`item.actions`]='{ item }'>
                  <div class="d-flex flex-wrap py-2 action-buttons">
                     <v-btn v-if="parseInt(item.applgradstatus) === 1" x-small color="success" dark class="mr-1 mb-1" :disabled="!approvalReady(item)" @click="approve(item)"><v-icon left x-small>mdi-check</v-icon>Approve</v-btn>
                     <div v-if="parseInt(item.applgradstatus) === 1 && !approvalReady(item)" class="caption orange--text text--darken-2 mb-1">
                        Waiting proposer/seconder approval
                     </div>
                     <v-btn v-if="parseInt(item.applgradstatus) === 1" x-small color="warning" dark class="mr-1 mb-1" @click="openReview(item, 'incomplete')"><v-icon left x-small>mdi-reload-alert</v-icon>Resubmit</v-btn>
                     <v-btn v-if="[1,4].includes(parseInt(item.applgradstatus))" x-small color="error" dark class="mb-1" @click="openReview(item, 'reject')"><v-icon left x-small>mdi-close</v-icon>Reject</v-btn>
                     <v-btn v-if="parseInt(item.applgradstatus) === 3" x-small color="secondary" dark class="mb-1" @click="reopen(item)"><v-icon left x-small>mdi-lock-open</v-icon>Reopen</v-btn>
                     <v-btn
                        v-if="canResetEndorsement(item)"
                        x-small
                        color="deep-purple darken-1"
                        dark
                        class="mr-1 mb-1"
                        :loading="resettingUserId === item.userid"
                        :disabled="!!resettingUserId"
                        @click="resetEndorsement(item)"
                     >
                        <v-icon left x-small>mdi-account-switch</v-icon>
                        Reset Endorsement
                     </v-btn>
                     <v-btn v-if="parseInt(item.applgradstatus) === 2" x-small color="deep-orange" dark class="mr-1 mb-1" @click="openReview(item, 'reverse')"><v-icon left x-small>mdi-undo</v-icon>Reverse</v-btn>
                     <span v-if="![1,4,5].includes(parseInt(item.applgradstatus))" class="caption grey--text">No action required</span>
                  </div>
               </template>

               <template v-slot:no-data><div class="pa-8 text-center grey--text"><v-icon size="48" color="grey lighten-1">mdi-school-outline</v-icon><div class="mt-2">No Graduate Upgrade applications found.</div></div></template>
            </v-data-table>
         </v-card>

         <v-dialog v-model="reviewDialog" max-width="520">
            <v-card>
               <v-card-title>{{ reviewAction === 'incomplete' ? 'Request Resubmission' : (reviewAction === 'reverse' ? 'Reverse Graduate Upgrade' : 'Reject Application') }}</v-card-title>
               <v-card-text>
                  <p v-if="selectedApplication">Applicant: <strong>{{ selectedApplication.fullname }}</strong></p>
                  <v-textarea v-model="reviewNotes" outlined label="Admin notes / reason *" rows="4" :error-messages="reviewError"></v-textarea>
               </v-card-text>
               <v-card-actions><v-spacer></v-spacer><v-btn text @click="reviewDialog=false">Cancel</v-btn><v-btn :color="reviewAction === 'incomplete' ? 'warning' : (reviewAction === 'reverse' ? 'deep-orange' : 'error')" dark :loading="submitting" @click="submitReview">Confirm</v-btn></v-card-actions>
            </v-card>
         </v-dialog>
      </v-container>
   </dashboard-layout>
</template>

<script>
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'
const axios = require('axios')

const MemberSummary = {
   props: ['member'],
   template: `<div v-if="member" class="py-2"><strong>{{ member.fullname }}</strong><div class="caption">{{ member.memberid || 'No ID' }} · {{ member.membertype }}</div><div class="caption grey--text">{{ member.contactemail }}</div></div><v-chip v-else x-small color="red" dark>Missing</v-chip>`,
}

export default {
   name: 'MOAGraduateUpgrades',
   components: { DashboardLayout, BaseBreadcrumb, MemberSummary },
   props: { applications: { type: Array, default: () => [] } },
   data() {
      return {
         page: { title: 'Graduate Upgrade Applications' },
         breadcrumbs: [{ text: 'Dashboard', disabled: false, to: '/dashboard' }, { text: 'Graduate Applications', disabled: true }],
         search: '',
         headers: [
            { text: 'Applicant', value: 'applicant' }, { text: 'Certificate', value: 'certificate', sortable: false },
            { text: 'New Proposer', value: 'proposer', sortable: false }, { text: 'New Seconder', value: 'seconder', sortable: false },
            { text: 'Status', value: 'applgradstatus' }, { text: 'Submitted', value: 'applgrad_submitted_at' },
            { text: 'Actions', value: 'actions', sortable: false },
         ],
         reviewDialog: false, selectedApplication: null, reviewAction: '', reviewNotes: '', reviewError: '', submitting: false,
         resettingUserId: null, actionMessage: '', actionMessageType: 'success',
      }
   },
   computed: {
      summaries() {
         const count = status => this.applications.filter(item => parseInt(item.applgradstatus) === status).length
         return [
            { label: 'Pending Review', value: count(1), color: 'orange darken-2', icon: 'mdi-clock-outline', className: 'pending-summary' },
            { label: 'Waiting Endorsement', value: count(5), color: 'indigo darken-1', icon: 'mdi-account-clock', className: 'endorsement-summary' },
            { label: 'Need Resubmission', value: count(4), color: 'deep-purple', icon: 'mdi-reload-alert', className: 'resubmit-summary' },
            { label: 'Pending Payment', value: count(2), color: 'blue darken-1', icon: 'mdi-cash-clock', className: 'approved-summary' },
            { label: 'Rejected', value: count(3), color: 'red', icon: 'mdi-close-circle-outline', className: 'rejected-summary' },
         ]
      },
      flashMessage() { return (this.$page && (this.$page.props || this.$page).flash && (this.$page.props || this.$page).flash.status) || '' },
   },
   methods: {
      statusLabel(value) { return ({ 1: 'Pending Review', 2: 'Pending Payment', 3: 'Rejected', 4: 'Need Resubmission', 5: 'Waiting Endorsement / Payment' })[parseInt(value)] || 'Unknown' },
      statusColor(value) { return ({ 1: 'orange darken-2', 2: 'blue darken-1', 3: 'red', 4: 'deep-purple', 5: 'indigo darken-1' })[parseInt(value)] || 'grey' },
      endorsementLabel(value) { return ({ 0: 'Pending', 1: 'Approved', 2: 'Rejected' })[parseInt(value || 0)] || 'Pending' },
      endorsementColor(value) { return ({ 0: 'orange darken-2', 1: 'green', 2: 'red' })[parseInt(value || 0)] || 'orange darken-2' },
      approvalReady(item) { return parseInt(item.applgrad_proposer_status || 0) === 1 && parseInt(item.applgrad_seconder_status || 0) === 1 },
      canResetEndorsement(item) {
         return parseInt(item.applgradstatus || 0) === 5
            && (parseInt(item.applgrad_proposer_status || 0) === 0
               || parseInt(item.applgrad_seconder_status || 0) === 0)
      },
      certificateUrl(item) { return `/${item.applgrad_certificatepath}/${item.applgrad_certificate}` },
      formatDate(value) { if (!value) return '-'; return new Date(value).toLocaleString('en-MY', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }) },
      approve(item) {
         if (!window.confirm(`Approve ${item.fullname} and change membership from Student to Graduate pending payment?`)) return
         this.$inertia.post('/admin/member/upgrade-graduate/approve', { userid: item.userid }, { preserveScroll: true })
      },
      openReview(item, action) { this.selectedApplication=item; this.reviewAction=action; this.reviewNotes=''; this.reviewError=''; this.reviewDialog=true },
      submitReview() {
         if (!this.reviewNotes.trim()) { this.reviewError='Admin notes are required.'; return }
         this.submitting=true
         this.$inertia.post(`/admin/member/upgrade-graduate/${this.reviewAction}`, { userid:this.selectedApplication.userid, notes:this.reviewNotes }, {
            preserveScroll:true, onSuccess:()=>{this.reviewDialog=false}, onFinish:()=>{this.submitting=false},
         })
      },
      reopen(item) {
         if (!window.confirm(`Reopen graduate upgrade application for ${item.fullname}?`)) return
         this.$inertia.post('/admin/member/upgrade-graduate/reopen', { userid: item.userid }, { preserveScroll: true })
      },
      resetEndorsement(item) {
         if (!this.canResetEndorsement(item)) return
         if (!window.confirm(`Reset proposer and seconder for ${item.fullname}? The member will need to select and submit new endorsers.`)) return

         this.resettingUserId = item.userid
         this.actionMessage = ''
         axios.post('/admin/decl/resetupgradeendorsement', {
            userid: item.userid,
            section: 'graduate_upgrade',
            role: 'proposer',
         }).then((resp) => {
            const ok = resp.data && resp.data.status === 'OK'
            this.actionMessageType = ok ? 'success' : 'error'
            this.actionMessage = ok
               ? (resp.data.message || 'Graduate Upgrade endorsement reset successfully.')
               : (resp.data.error || 'Unable to reset Graduate Upgrade endorsement.')

            if (ok) {
               Object.assign(item, {
                  applgradstatus: 4,
                  applgrad_proposer_userid: null,
                  applgrad_seconder_userid: null,
                  applgrad_proposer_status: 0,
                  applgrad_seconder_status: 0,
                  applgrad_proposer_responded_at: null,
                  applgrad_seconder_responded_at: null,
                  graduate_proposer: null,
                  graduate_seconder: null,
               })
            }
         }).catch((error) => {
            this.actionMessageType = 'error'
            this.actionMessage = error.response && error.response.data && error.response.data.error
               ? error.response.data.error
               : 'Unable to reset Graduate Upgrade endorsement.'
         }).finally(() => {
            this.resettingUserId = null
         })
      },
   },
}
</script>

<style scoped>
.summary-card { border-left: 4px solid #90a4ae !important; }.pending-summary{border-left-color:#ef6c00!important}.endorsement-summary{border-left-color:#3949ab!important}.resubmit-summary{border-left-color:#673ab7!important}.approved-summary{border-left-color:#2e7d32!important}.rejected-summary{border-left-color:#c62828!important}
.admin-search{max-width:280px}.notes-cell{max-width:200px;white-space:normal}.action-buttons{min-width:205px}
</style>
